import { type PartialDeep } from 'type-fest';

import {
  type CanvasElementFrom,
  type PartialPropsOf,
  type PropsOf,
} from '@blaze-runtime/engine-domain';

import {
  getSourceProps,
  getSourceTransforms,
} from '../../canvas-elements/element-model/transformations';
import { parkedToggleButtonProps } from '../toggle-button/toggle-button.transforms';

type CheckboxElement = CanvasElementFrom<'checkbox'>;
type CheckboxProps = PartialPropsOf<'checkbox'>;

function transformDropdownToCheckbox(source: CanvasElementFrom<'dropdown'>): CheckboxElement {
  const props = getSourceProps(source);
  const propsT = getSourceTransforms<'dropdown', 'checkbox'>(source, 'checkbox');
  const newProps: CheckboxProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      data: {
        defaultValue: props.content?.data?.defaultValue,
      },
      checkbox: {
        values: props.content?.menu?.values,
        preselect: props.content?.menu?.preselect ? [props.content.menu.preselect] : undefined,
        layout: propsT?.content?.checkbox?.layout,
        readonly: propsT?.content?.checkbox?.readonly,
        minWidth: propsT?.content?.checkbox?.minWidth,
        maxColumns: propsT?.content?.checkbox?.maxColumns,
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      fontFamily: props.style?.fontFamily,
      lineHeight: props.style?.lineHeight,
      general: {
        title: props.style?.general?.title,
        accent: props.style?.general?.accent,
        offset: props.style?.general?.offset,
      },
      checkbox: {
        label: props.style?.input?.color,
        symbol: propsT?.style?.checkbox?.symbol,
        fill: props.style?.input?.fill,
        border: props.style?.input?.border,
        radius: props.style?.input?.radius,
      },
    },
    transforms: {
      dropdown: {
        content: {
          menu: {
            placeholder: props.content?.menu?.placeholder,
            preselect: props.content?.menu?.preselect,
            prefix: props.content?.menu?.prefix,
            suffix: props.content?.menu?.suffix,
            dynamicSearch: props.content?.menu?.dynamicSearch,
          },
        },
        style: {
          list: props.style?.list,
        },
      } as PartialDeep<PropsOf<'dropdown'>>,
    },
  };

  return { props: newProps } as any;
}

function transformTagsToCheckbox(source: CanvasElementFrom<'tags'>): CheckboxElement {
  const props = getSourceProps(source);
  const newProps: CheckboxProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      data: {
        defaultValue: props.content?.data?.defaultValue,
      },
      checkbox: {
        values: props.content?.tags?.values,
        preselect: props.content?.tags?.preselect,
        layout: undefined, // no mapping,
        readonly: props.content?.options?.readonly,
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
        accent: props.style?.general?.accent,
      },
      checkbox: {
        label: props.style?.input?.color,
        symbol: undefined, // no mapping
        fill: props.style?.input?.fill,
        border: props.style?.input?.border,
        radius: props.style?.input?.radius,
      },
    },
  };

  return { props: newProps } as any;
}

function transformToggleButtonToCheckbox(
  source: CanvasElementFrom<'toggleButton'>,
): CheckboxElement {
  const props = getSourceProps(source);
  const propsT = getSourceTransforms<'toggleButton', 'checkbox'>(source, 'checkbox');
  const newProps: CheckboxProps = {
    content: {
      title: { ...props.content?.title },
      data: {
        defaultValue: propsT?.content?.data?.defaultValue,
      },
      checkbox: {
        values: propsT?.content?.checkbox?.values,
        preselect: propsT?.content?.checkbox?.preselect,
        layout: propsT?.content?.checkbox?.layout,
        readonly: propsT?.content?.checkbox?.readonly,
        minWidth: propsT?.content?.checkbox?.minWidth,
        maxColumns: propsT?.content?.checkbox?.maxColumns,
      },
    },
    style: {
      general: {
        title: props.style?.general?.title,
        accent: propsT?.style?.general?.accent,
        offset: props.style?.button?.offset,
      },
      checkbox: {
        label: propsT?.style?.checkbox?.label,
        symbol: propsT?.style?.checkbox?.symbol,
        fill: propsT?.style?.checkbox?.fill,
        border: propsT?.style?.checkbox?.border,
        radius: propsT?.style?.checkbox?.radius,
      },
    },
    transforms: {
      toggleButton: parkedToggleButtonProps(props),
    },
  };

  return { props: newProps } as unknown as CheckboxElement;
}

export const transformToCheckbox = {
  transformDropdownToCheckbox,
  transformTagsToCheckbox,
  transformToggleButtonToCheckbox,
};
